import { z } from "zod";
import { DashboardInviteStatus } from "./users";

/** Request body for the private admin membership creation endpoint. */
export const CreateOrganizationMembershipRequestSchema = z.object({
  userId: z.string().trim().min(1),
  organizationId: z.string().trim().min(1),
  role: z.enum(["ADMIN", "USER"]),
  roleId: z.string().trim().min(1).optional(),
});

export type CreateOrganizationMembershipRequest = z.infer<
  typeof CreateOrganizationMembershipRequestSchema
>;

/** Membership returned after WorkOS and Forgepoint access are created. */
export const CreatedOrganizationMembershipSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  role: z.enum(["ADMIN", "USER"]),
  roleId: z.string().nullable().optional(),
  status: z.literal("ACTIVE"),
  dashboardInviteStatus: z.literal(DashboardInviteStatus.INVITED),
  externalMembershipId: z.string().min(1),
});

export type CreatedOrganizationMembership = z.infer<
  typeof CreatedOrganizationMembershipSchema
>;

/** Response for POST /admin/organization-memberships. */
export const CreateOrganizationMembershipResponseSchema = z.object({
  membership: CreatedOrganizationMembershipSchema,
});

export type CreateOrganizationMembershipResponse = z.infer<
  typeof CreateOrganizationMembershipResponseSchema
>;

/** Request body for the private admin membership removal endpoint. */
export const RemoveOrganizationMembershipRequestSchema = z.object({
  userId: z.string().trim().min(1),
  organizationId: z.string().trim().min(1),
});

export type RemoveOrganizationMembershipRequest = z.infer<
  typeof RemoveOrganizationMembershipRequestSchema
>;

/** Membership returned after WorkOS and Forgepoint access are deleted. */
export const RemovedOrganizationMembershipSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  role: z.enum(["ADMIN", "USER"]),
  externalMembershipId: z.string().nullable(),
});

export type RemovedOrganizationMembership = z.infer<
  typeof RemovedOrganizationMembershipSchema
>;

/** Response for DELETE /admin/organization-memberships. */
export const RemoveOrganizationMembershipResponseSchema = z.object({
  membership: RemovedOrganizationMembershipSchema,
});

export type RemoveOrganizationMembershipResponse = z.infer<
  typeof RemoveOrganizationMembershipResponseSchema
>;

/** Request body for updating an existing membership's role. */
export const UpdateOrganizationMembershipRequestSchema =
  CreateOrganizationMembershipRequestSchema;

/** A role change scoped to one user and organization. */
export type UpdateOrganizationMembershipRequest = z.infer<
  typeof UpdateOrganizationMembershipRequestSchema
>;

/** Current or updated role of one organization membership. */
export const OrganizationMembershipRoleResponseSchema = z.object({
  membership: CreateOrganizationMembershipRequestSchema,
});

/** Response for reading or updating a membership role. */
export type OrganizationMembershipRoleResponse = z.infer<
  typeof OrganizationMembershipRoleResponseSchema
>;

/** One organization the authenticated user belongs to. */
export const OrganizationMembershipSchema = z.object({
  organizationId: z.string(),
  organizationName: z.string(),
  organizationLogoUrl: z.string().nullable(),
  roleName: z.string(),
  isActive: z.boolean(),
});

export type OrganizationMembership = z.infer<
  typeof OrganizationMembershipSchema
>;

/** Response for GET /users/me/organizations. */
export const MyOrganizationsResponseSchema = z.object({
  organizations: z.array(OrganizationMembershipSchema),
});

export type MyOrganizationsResponse = z.infer<
  typeof MyOrganizationsResponseSchema
>;

/** Request body for PUT /auth/session/organization. */
export const SwitchOrganizationRequestSchema = z.object({
  organizationId: z.string(),
});

export type SwitchOrganizationRequest = z.infer<
  typeof SwitchOrganizationRequestSchema
>;

/** Response for PUT /auth/session/organization. */
export const SwitchOrganizationResponseSchema = z.object({
  sessionToken: z.string(),
});

export type SwitchOrganizationResponse = z.infer<
  typeof SwitchOrganizationResponseSchema
>;
